import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AlertTriangle, Loader2, ToggleRight } from 'lucide-react'
import { ApiService } from '@/services/api'
import { RESTAURANT_FEATURES, type FeatureItem } from '@/lib/features'
import { cn } from '@/lib/utils'
import type { Restaurant } from '@/types'

export default function FeatureListScreen() {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [selectedId, setSelectedId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadRestaurants = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getAllRestaurants()
      if (response.success) {
        setRestaurants(response.data)
        setSelectedId(prev => prev || response.data[0]?._id || '')
      } else {
        setError(response.message || 'Failed to load restaurants')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load restaurants')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadRestaurants()
  }, [])

  const selected = restaurants.find(r => r._id === selectedId)
  const disabled = new Set(selected?.disabledFeatures ?? [])

  const setDisabledFeatures = (restaurantId: string, disabledFeatures: string[]) =>
    setRestaurants(prev => prev.map(r => r._id === restaurantId ? { ...r, disabledFeatures } : r))

  const handleToggle = async (key: string, enabled: boolean) => {
    if (!selected) return
    const previous = selected.disabledFeatures ?? []
    const next = enabled ? previous.filter(k => k !== key) : Array.from(new Set([...previous, key]))

    setSavingKey(key)
    setError(null)
    // Optimistic update, reverted on failure below
    setDisabledFeatures(selected._id, next)
    try {
      const response = await ApiService.updateRestaurant(selected._id, { disabledFeatures: next })
      if (!response.success) {
        throw new Error(response.message || 'Failed to update feature')
      }
      // Trust what the server actually stored (it drops unknown keys)
      setDisabledFeatures(selected._id, response.data?.disabledFeatures ?? next)
    } catch (err: any) {
      setDisabledFeatures(selected._id, previous)
      setError(err.response?.data?.message || err.message || 'Failed to update feature')
    } finally {
      setSavingKey(null)
    }
  }

  const renderRow = (feature: FeatureItem, isChild = false, parentOff = false) => {
    const isOn = !disabled.has(feature.key)
    return (
      <div
        key={feature.key}
        className={cn('flex items-center justify-between py-3', isChild && 'pl-8')}
      >
        <div className="flex items-center gap-2">
          <Label
            htmlFor={`feature-${feature.key}`}
            className={cn('text-sm font-medium', (parentOff || !isOn) && 'text-muted-foreground')}
          >
            {feature.label}
          </Label>
          {!isOn && <Badge variant="outline" className="text-xs">Hidden</Badge>}
        </div>
        <Switch
          id={`feature-${feature.key}`}
          checked={isOn}
          disabled={savingKey !== null}
          onCheckedChange={(checked) => handleToggle(feature.key, checked)}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Feature List</h1>
        <p className="text-muted-foreground">
          Choose which menu items each restaurant can see. Everything is on by default.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Restaurant</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger className="max-w-md">
                <SelectValue placeholder="Select a restaurant" />
              </SelectTrigger>
              <SelectContent>
                {restaurants.map(r => (
                  <SelectItem key={r._id} value={r._id}>
                    {r.name}{r.address?.city ? ` — ${r.address.city}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {selected && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <ToggleRight className="h-5 w-5" />
              <span>Menu items — {selected.name}</span>
              {savingKey && <Loader2 className="h-4 w-4 animate-spin" />}
            </CardTitle>
            <CardDescription>
              Turned-off items disappear from this restaurant's side menu and can't be opened.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {RESTAURANT_FEATURES.map(feature => (
                <React.Fragment key={feature.key}>
                  {renderRow(feature)}
                  {feature.children?.map(child => renderRow(child, true, disabled.has(feature.key)))}
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
